import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { DocumentsHttpClient } from '@omnea-app/documents/documentsHttpClient'
import { useToken } from '@omnea-app/fe-auth/TokenProvider'
import { FileValidationStatus } from '@omnea-app/fe-legacy/components/shared/file/types'
import { isNotFoundApiError } from '@omnea-app/packages/universal/error-handling/isApiErrorOfType'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'

const INITIAL_POLL_INTERVAL_MS = 1000
const MAX_POLL_INTERVAL_MS = 8000
const MAX_POLL_DURATION_MS = 30_000
const STILL_SCANNING_THRESHOLD_MS = 10_000

const TERMINAL_STATUSES: readonly FileValidationStatus[] = ['VALIDATED', 'QUARANTINED', 'NOT_FOUND']

export const calculatePollInterval = ({
  validationStatus,
  enabledAt,
  now,
  updateCount,
  maxPollDurationMs = MAX_POLL_DURATION_MS,
}: {
  validationStatus: FileValidationStatus | undefined
  enabledAt: number | null
  now: number
  updateCount: number
  maxPollDurationMs?: number
}): number | false => {
  if (validationStatus && TERMINAL_STATUSES.includes(validationStatus)) return false
  if (enabledAt == null) return false
  if (now - enabledAt >= maxPollDurationMs) return false

  return Math.min(INITIAL_POLL_INTERVAL_MS * 2 ** Math.max(0, updateCount - 1), MAX_POLL_INTERVAL_MS)
}

export const fileStatusQueryKey = (filestoreId: string, documentId?: string) =>
  ['fileStatus', filestoreId, documentId ?? null] as const

export type FileStatusResult =
  | { validationStatus: 'VALIDATED'; signedUrl: string }
  | { validationStatus: 'PENDING' }
  | { validationStatus: 'QUARANTINED' }
  | { validationStatus: 'NOT_FOUND' }

export const useFileStatusQuery = ({
  filestoreId,
  documentId,
  enabled,
  gcTime = 0,
  staleTime,
  maxPollDurationMs = MAX_POLL_DURATION_MS,
}: {
  filestoreId: string
  documentId?: string
  enabled: boolean
  gcTime?: number
  staleTime?: number
  maxPollDurationMs?: number
}) => {
  const { getToken } = useToken()
  const queryClient = useQueryClient()

  const queryEnabled = enabled && filestoreId !== ''
  const enabledAtRef = useRef<number | null>(null)
  const [isStillScanning, setIsStillScanning] = useState(false)
  const [isTimeout, setIsTimeout] = useState(false)

  const retainInCache = gcTime > 0

  useEffect(() => {
    if (!queryEnabled) {
      enabledAtRef.current = null
      setIsStillScanning(false)
      setIsTimeout(false)
      return
    }

    enabledAtRef.current = Date.now()

    // TODO: The gcTime:0 + removeQueries combo works around consumers (useFileValidation,
    // useDownloadDocument) syncing query data into local state via useEffect. If those
    // consumers derived state directly from query.data instead, we could drop this and let
    // TQ's cache resolve already-validated files instantly. Address when extending to the
    // upload flow — re-polling validated files on every enable won't scale.
    //
    // gcTime/retainInCache is a workaround for using this elsewhere, still need to fix those other consumers
    //
    // Only a terminal result (e.g. an already-VALIDATED signed URL) is worth retaining so it
    // resolves instantly on re-mount. A retained PENDING result would be treated as fresh under
    // a long staleTime and short-circuit the poll, leaving the image stuck; clear it so the query
    // re-enters the polling loop from scratch.
    const cached = queryClient.getQueryData<FileStatusResult>(fileStatusQueryKey(filestoreId, documentId))
    const cachedIsTerminal = cached != null && TERMINAL_STATUSES.includes(cached.validationStatus)
    if (!retainInCache || !cachedIsTerminal) {
      queryClient.removeQueries({ queryKey: fileStatusQueryKey(filestoreId, documentId), exact: true })
    }

    const stillScanningTimer = setTimeout(() => setIsStillScanning(true), STILL_SCANNING_THRESHOLD_MS)
    const timeoutTimer = setTimeout(() => setIsTimeout(true), maxPollDurationMs)

    return () => {
      clearTimeout(stillScanningTimer)
      clearTimeout(timeoutTimer)
      setIsStillScanning(false)
      setIsTimeout(false)
    }
  }, [queryEnabled, filestoreId, documentId, queryClient, retainInCache, maxPollDurationMs])

  const query = useQuery({
    queryKey: fileStatusQueryKey(filestoreId, documentId),
    enabled: queryEnabled,
    gcTime,
    staleTime,
    retry: false,
    refetchInterval: (q) =>
      calculatePollInterval({
        validationStatus: q.state.data?.validationStatus,
        enabledAt: enabledAtRef.current,
        now: Date.now(),
        updateCount: q.state.dataUpdateCount + q.state.errorUpdateCount,
        maxPollDurationMs,
      }),
    queryFn: async (): Promise<FileStatusResult> => {
      const token = await getToken()
      const documentsClient = new DocumentsHttpClient({ authorization: token })

      try {
        const { data, error } = await documentsClient.GET('/signed-url', {
          params: { query: { filestoreId, id: documentId } },
        })

        if (error?.type === 'ForbiddenError') {
          return { validationStatus: 'QUARANTINED' }
        }

        throwIfApiError(error)

        if (!data || data.validationStatus === 'PENDING') {
          return { validationStatus: 'PENDING' }
        }

        return { validationStatus: 'VALIDATED', signedUrl: data.signedUrl }
      } catch (error) {
        if (isNotFoundApiError(error)) {
          return { validationStatus: 'NOT_FOUND' }
        }
        throw error
      }
    },
  })

  const validationStatus = query.data?.validationStatus
  const isTerminal = validationStatus != null && TERMINAL_STATUSES.includes(validationStatus)

  return {
    validationStatus: validationStatus ?? null,
    signedUrl: query.data?.validationStatus === 'VALIDATED' ? query.data.signedUrl : undefined,
    isStillScanning: !isTerminal && isStillScanning,
    isTimeout: !isTerminal && isTimeout,
    isError: query.isError,
  }
}
